import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { AppDispatch, RootState } from 'store'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { SquareCheckBox } from 'components/CheckBox/CheckBox.styled'
import { CheckBoxIcon } from 'components/CheckBox/CheckBoxIcon'
import { ISelectValue } from 'constants/globalTypes'
import { addInventoryAssignment, getInventoryAssignments } from 'features/inventory/inventoryItemsSlice'
import { Flex, ItemsGrid } from 'styling/GlobalStyles'
import { OtherText, TitleText } from 'styling/Typography'
import { formatDateForDisplay, getParams } from 'utils/helpers'
import { IGetAllInventoryAssignmentsSingleResponse } from 'types/api/inventoryItems.interface'
import {
    IStoreCustodyRow,
    IStoreReturnRow,
    IStoreReturnTabProps,
} from 'types/inventoryCounter.interface'
import {
    counterLinesSummary,
    CUSTODY_STATUS_META,
    RETURNS_EMPTY_CAPTION,
    RETURNS_LOCAL_CAPTION,
    RETURNS_REAL_CAPTION,
} from '../inventoryCounterDemoData'
import {
    BasketLineSku,
    CustodyItemText,
    LedgerMetaText,
    NatureNote,
    RecentReturnRow,
    ReturnLineInfo,
    ReturnLineMeta,
    ReturnLineRow,
} from './StoreCounter.styled'

const RETURN_TAB_HEADER_LINE =
    'Returnables coming back to the store — ticked lines go back on stock.'

const custodySkuParts = (label: string): { sku: string | null; name: string } => {
    const match = label.match(/^([A-Z]{2,}-\d{2,})\s+(.+)$/)
    return match ? { sku: match[1], name: match[2] } : { sku: null, name: label }
}

export const ReturnTab = ({
    custodyRows,
    onDemoReturn,
    returnFocus,
    onReturnFocusHandled,
    localLedger,
}: IStoreReturnTabProps) => {
    const dispatch = useDispatch<AppDispatch>()
    const { assignments } = useSelector((store: RootState) => store.inventoryItems)
    const [returnWorker, setReturnWorker] = useState<ISelectValue | null>(null)
    const [returnTicks, setReturnTicks] = useState<string[]>([])
    const [realReturnTicks, setRealReturnTicks] = useState<number[]>([])
    const [returnKey, setReturnKey] = useState(0)

    useEffect(() => {
        if (!returnFocus) return
        if (returnFocus.assignmentId) {
            setRealReturnTicks((prev) =>
                prev.includes(returnFocus.assignmentId) ? prev : [...prev, returnFocus.assignmentId],
            )
            return
        }
        setReturnTicks((prev) =>
            prev.includes(returnFocus.custodyId) ? prev : [...prev, returnFocus.custodyId],
        )
    }, [returnFocus])

    const visibleCustody: IStoreCustodyRow[] = (custodyRows ?? []).filter(
        (row) =>
            row.status !== 'RETURNED' &&
            (returnWorker
                ? row.holder === returnWorker.label
                : returnFocus
                    ? row.holder === returnFocus.holder
                    : false),
    )

    const realReturnCandidates: IGetAllInventoryAssignmentsSingleResponse[] =
        returnWorker?.value || returnFocus?.assignmentId
            ? (assignments?.data || []).filter((assignment) => assignment.type == 1 && assignment.status == 2)
            : []

    const custodyReturnRows: IStoreReturnRow[] = visibleCustody.map((row) => ({
        id: row.id,
        source: 'CUSTODY' as const,
        itemLabel: row.item,
        metaLabel: row.dueLabel,
        holderLabel: row.holder,
        custodyRef: row,
    }))

    const realReturnRows: IStoreReturnRow[] = realReturnCandidates.map((assignment) => ({
        id: `ASG-${assignment.id}`,
        source: 'REAL' as const,
        itemLabel: counterLinesSummary(
            (assignment.lines || []).map((line) => ({
                quantity: line.quantity,
                name: line.inventoryItem?.name ?? 'Item',
            })),
        ),
        metaLabel: `Assigned ${formatDateForDisplay(assignment.createdAt)}`,
        holderLabel: '—',
        assignmentRef: assignment,
    }))

    const isReturnRowTicked = (row: IStoreReturnRow): boolean =>
        row.source === 'CUSTODY'
            ? returnTicks.includes(row.id)
            : realReturnTicks.includes(row.assignmentRef.id)

    const toggleReturnRow = (row: IStoreReturnRow) => {
        if (row.source === 'CUSTODY') {
            setReturnTicks((prev) =>
                prev.includes(row.id) ? prev.filter((id) => id !== row.id) : [...prev, row.id],
            )
        } else {
            const assignmentId = row.assignmentRef.id
            setRealReturnTicks((prev) =>
                prev.includes(assignmentId)
                    ? prev.filter((id) => id !== assignmentId)
                    : [...prev, assignmentId],
            )
        }
    }

    const handleConfirmReturn = async () => {
        const tickedCustody = visibleCustody.filter((row) => returnTicks.includes(row.id))
        const tickedReal = realReturnCandidates.filter((assignment) =>
            realReturnTicks.includes(assignment.id),
        )
        if (!tickedCustody.length && !tickedReal.length) {
            toast.warn('Tick at least one line to return')
            return
        }
        if (tickedReal.length && !returnWorker?.value) {
            toast.warn('Pick the worker returning — live returns need one')
            return
        }

        let allFulfilled = true

        for (const assignment of tickedReal) {
            const resp = await dispatch(
                addInventoryAssignment({
                    type: { label: 'Return', value: 2 },
                    location: assignment.location
                        ? { value: assignment.location.id, label: assignment.location.name }
                        : null,
                    assigneeUser: returnWorker,
                    notes: '',
                    lines: (assignment.lines || []).map((line) => ({
                        id: line.id,
                        item: {
                            value: line.inventoryItem?.id ?? line.inventoryItemId,
                            label: line.inventoryItem?.name ?? 'Item',
                        },
                        quantity: line.quantity,
                        serials: (line.serials || []).map((serial) => ({
                            id: serial.id,
                            label: '',
                            value: serial.serialId,
                        })),
                    })),
                }),
            )
            if (resp.meta.requestStatus !== 'fulfilled') allFulfilled = false
        }

        if (tickedReal.length && allFulfilled) {
            dispatch(getInventoryAssignments(getParams({ location, limit: 30 })))
            toast.success(
                `Return request filed for ${tickedReal.length} ${tickedReal.length === 1 ? 'assignment' : 'assignments'} — ${returnWorker.label}`,
            )
        }

        if (tickedCustody.length) {
            const nowISO = new Date().toISOString()
            onDemoReturn?.(
                tickedCustody.map((row) => row.id),
                tickedCustody.map((row) => ({
                    id: `LOC-R-${row.id}`,
                    kind: 'RETURNED',
                    dateISO: nowISO,
                    worker: row.holder,
                    itemsSummary: row.item,
                    chargeLabel: null,
                    dueBackLabel: null,
                    valueGhs: null,
                })),
            )
            tickedCustody.forEach((row) => toast.success(`Returned — ${row.item} back on stock`))
        }

        if (!allFulfilled) return

        setReturnTicks([])
        setRealReturnTicks([])
        setReturnWorker(null)
        setReturnKey((prev) => prev + 1)
        onReturnFocusHandled?.()
    }

    const custodyColumns: IHonestTableColumn<IStoreReturnRow>[] = [
        {
            id: 'item',
            header: 'ITEM',
            minWidth: '16rem',
            cell: (row) => <CustodyItemText>{custodySkuParts(row.itemLabel).name}</CustodyItemText>,
        },
        {
            id: 'sku',
            header: 'SKU',
            cell: (row) => {
                const sku = custodySkuParts(row.itemLabel).sku
                return sku ? <BasketLineSku>{sku}</BasketLineSku> : <OtherText grey>—</OtherText>
            },
        },
        {
            id: 'due',
            header: 'ASSIGNED / DUE BACK',
            minWidth: '12rem',
            cell: (row) => <LedgerMetaText>{row.metaLabel}</LedgerMetaText>,
        },
        {
            id: 'status',
            header: 'STATUS',
            cell: (row) =>
                row.custodyRef?.status === 'OVERDUE' ? (
                    <StatusPill
                        status={CUSTODY_STATUS_META[row.custodyRef.status].tone}
                        dot={CUSTODY_STATUS_META[row.custodyRef.status].dot}
                    >
                        {row.custodyRef.pillLabel}
                    </StatusPill>
                ) : (
                    <OtherText grey>—</OtherText>
                ),
        },
        {
            id: 'return',
            header: 'RETURN',
            align: 'right',
            cell: (row) => {
                const ticked = isReturnRowTicked(row)
                return <SquareCheckBox active={ticked}>{ticked && <CheckBoxIcon />}</SquareCheckBox>
            },
        },
    ]

    const recentReturns = localLedger.filter((row) => row.kind === 'RETURNED')

    return (
        <Flex column gap='1rem'>
            <OtherText grey>{RETURN_TAB_HEADER_LINE}</OtherText>
            <Container>
                <ContainerHeader>
                    <TitleText>Return</TitleText>
                </ContainerHeader>
                <ContainerContent gap='1rem'>
                    <ItemsGrid columns={4}>
                        <HonestSelectAsyncInput
                            key={`return-worker-${returnKey}`}
                            link='users/basic'
                            labelText='Worker'
                            name='returnWorker'
                            onChange={(v: ISelectValue) => setReturnWorker(v?.value ? v : null)}
                            parserFunction={(data: any) => data.data.map((i: any) => ({ label: i.fullName, value: i.id }))}
                            defaultValue={returnWorker?.label}
                            nomarg
                        />
                    </ItemsGrid>
                    {returnFocus && !returnWorker && (
                        <OtherText grey>
                            {returnFocus.assignmentId
                                ? 'Processing a live assignment return — pick the worker returning.'
                                : `Processing return for ${returnFocus.holder}`}
                        </OtherText>
                    )}
                    {!returnWorker && !returnFocus ? (
                        <OtherText grey>{RETURNS_EMPTY_CAPTION}</OtherText>
                    ) : !custodyReturnRows.length && !realReturnRows.length ? (
                        <OtherText grey>
                            {`Nothing outstanding for ${returnWorker?.label || returnFocus?.holder}.`}
                        </OtherText>
                    ) : (
                        <Flex column gap='1rem'>
                            {custodyReturnRows.length > 0 && (
                                <HonestTable
                                    columns={custodyColumns}
                                    data={custodyReturnRows}
                                    keyField='id'
                                    onClick={toggleReturnRow}
                                />
                            )}
                            {realReturnRows.length > 0 && (
                                <Flex column>
                                    {realReturnRows.map((row) => {
                                        const ticked = isReturnRowTicked(row)
                                        return (
                                            <ReturnLineRow key={row.id} onClick={() => toggleReturnRow(row)}>
                                                <SquareCheckBox active={ticked}>{ticked && <CheckBoxIcon />}</SquareCheckBox>
                                                <ReturnLineInfo>
                                                    <CustodyItemText>{row.itemLabel}</CustodyItemText>
                                                    <ReturnLineMeta>{row.metaLabel}</ReturnLineMeta>
                                                </ReturnLineInfo>
                                                <LedgerMetaText>{row.holderLabel}</LedgerMetaText>
                                            </ReturnLineRow>
                                        )
                                    })}
                                </Flex>
                            )}
                        </Flex>
                    )}
                    <Flex between center gap='1rem'>
                        <Flex column gap='0.25rem'>
                            <NatureNote>{RETURNS_LOCAL_CAPTION}</NatureNote>
                            {realReturnCandidates.length > 0 && <NatureNote>{RETURNS_REAL_CAPTION}</NatureNote>}
                        </Flex>
                        <Button variant='primary' onClick={handleConfirmReturn}>
                            Confirm return
                        </Button>
                    </Flex>
                </ContainerContent>
            </Container>
            {recentReturns.length > 0 && (
                <Container>
                    <ContainerHeader>
                        <TitleText>Returned this session</TitleText>
                    </ContainerHeader>
                    <ContainerContent>
                        <Flex column>
                            {recentReturns.map((row) => (
                                <RecentReturnRow key={row.id}>
                                    <ReturnLineInfo>
                                        <CustodyItemText>{row.itemsSummary}</CustodyItemText>
                                        <ReturnLineMeta>
                                            {`${row.worker || '—'} · ${formatDateForDisplay(row.dateISO, true)}`}
                                        </ReturnLineMeta>
                                    </ReturnLineInfo>
                                    <StatusPill status='success'>Returned</StatusPill>
                                </RecentReturnRow>
                            ))}
                        </Flex>
                    </ContainerContent>
                </Container>
            )}
        </Flex>
    )
}
